/**
 * What the user picked or dropped for upload: the files, each file's path
 * relative to the drop/pick root (`"Plans/Level 1/a.pdf"`, or just the name for
 * a loose file), and every directory seen on the way — including the empty ones,
 * which no file path can stand in for.
 *
 * `directoryPaths` is what lets an upload create its whole folder tree up front,
 * before the first byte goes up, and lets a tree of empty folders be uploaded at
 * all. It is only as complete as the source allows: a drop (Entry API) and the
 * File System Access picker both enumerate directories, but a plain
 * `<input webkitdirectory>` reports files only, so there it is just what the
 * file paths imply.
 */
export interface UploadSelection {
  files: File[];
  relativePaths: string[];
  directoryPaths: string[];
}

/** A selection holding neither a file nor a folder — nothing to upload. */
export function isEmptySelection(selection: UploadSelection): boolean {
  return selection.files.length === 0 && selection.directoryPaths.length === 0;
}

/** Files chosen through a plain `<input type="file">` (with or without `webkitdirectory`). */
export function selectionFromFileList(fileList: FileList | File[] | null): UploadSelection {
  const files = Array.from(fileList ?? []);
  return {
    files,
    relativePaths: files.map((file) => file.webkitRelativePath || file.name),
    directoryPaths: [],
  };
}

/**
 * Every folder an upload has to create, parents before children: each file
 * path's ancestors plus the explicit directory paths. Paths use "/" and carry no
 * leading or trailing slash; duplicates collapse.
 */
export function collectFolderPaths(relativePaths: string[], directoryPaths: string[]): string[] {
  const folders = new Set<string>();
  const addWithAncestors = (segments: string[]) => {
    for (let depth = 1; depth <= segments.length; depth++) {
      folders.add(segments.slice(0, depth).join("/"));
    }
  };

  for (const path of relativePaths) {
    const segments = path.split("/").filter(Boolean);
    // The last segment is the file itself.
    addWithAncestors(segments.slice(0, -1));
  }
  for (const path of directoryPaths) {
    addWithAncestors(path.split("/").filter(Boolean));
  }

  return Array.from(folders).sort(
    (a, b) => a.split("/").length - b.split("/").length || a.localeCompare(b)
  );
}

/** Entry API paths are absolute within the drop (`/Plans/a.pdf`); ours are relative. */
const stripLeadingSlash = (path: string) => (path.startsWith("/") ? path.slice(1) : path);

/**
 * Reads everything out of a drop's DataTransfer, traversing dropped folders
 * recursively via the FileSystem Entry API so directory uploads keep their
 * relative paths and their empty sub-folders. Falls back to the flat
 * `dataTransfer.files` list on browsers without the Entry API.
 *
 * CRITICAL: all `webkitGetAsEntry()` calls happen synchronously before the
 * first `await` — DataTransferItem references are invalidated the moment the
 * drop handler returns, so resolving them inside an async loop would yield
 * `null` for every item after the first.
 */
export async function extractDroppedFiles(dataTransfer: DataTransfer): Promise<UploadSelection> {
  const items = Array.from(dataTransfer.items);

  if (items.length > 0 && typeof items[0].webkitGetAsEntry === "function") {
    const rootEntries: FileSystemEntry[] = [];
    for (const item of items) {
      if (item.kind === "file") {
        const entry = item.webkitGetAsEntry();
        if (entry) rootEntries.push(entry);
      }
    }

    // Deduplicate by name+size+lastModified, preferring paths that include a
    // folder so a directory upload keeps its structure.
    const fileMap = new Map<string, { file: File; path: string }>();
    const directoryPaths: string[] = [];

    // readEntries() returns at most ~100 entries per call (Chromium) / ~64
    // (Safari); call repeatedly until it returns an empty batch.
    const readAllEntries = (dirReader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> =>
      new Promise((resolve, reject) => {
        const all: FileSystemEntry[] = [];
        const readBatch = () => {
          dirReader.readEntries((batch) => {
            if (batch.length === 0) {
              resolve(all);
            } else {
              all.push(...batch);
              readBatch();
            }
          }, reject);
        };
        readBatch();
      });

    const traverseEntry = async (entry: FileSystemEntry): Promise<void> => {
      if (entry.isFile) {
        const file = await new Promise<File>((resolve, reject) =>
          (entry as FileSystemFileEntry).file(resolve, reject)
        );
        const relativePath = stripLeadingSlash(entry.fullPath);
        const key = `${file.name}-${file.size}-${file.lastModified}`;
        if (!fileMap.has(key) || relativePath.includes("/")) {
          fileMap.set(key, { file, path: relativePath });
        }
      } else if (entry.isDirectory) {
        directoryPaths.push(stripLeadingSlash(entry.fullPath));
        const dirReader = (entry as FileSystemDirectoryEntry).createReader();
        const entries = await readAllEntries(dirReader);
        for (const childEntry of entries) {
          await traverseEntry(childEntry);
        }
      }
    };

    for (const entry of rootEntries) {
      await traverseEntry(entry);
    }

    const collected = Array.from(fileMap.values());
    return {
      files: collected.map((e) => e.file),
      relativePaths: collected.map((e) => e.path),
      directoryPaths,
    };
  }

  // Fallback for browsers without the FileSystem Entry API.
  return selectionFromFileList(dataTransfer.files);
}

/**
 * The slice of the File System Access API the folder picker needs. Declared here
 * because the project's `lib` has neither `showDirectoryPicker` (not in
 * lib.dom at all) nor the async iteration of directory handles
 * (lib.dom.asynciterable).
 */
interface IterableDirectoryHandle {
  readonly kind: "directory";
  readonly name: string;
  values(): AsyncIterable<IterableDirectoryHandle | FileHandle>;
}
interface FileHandle {
  readonly kind: "file";
  readonly name: string;
  getFile(): Promise<File>;
}
type WindowWithDirectoryPicker = Window & {
  showDirectoryPicker?: (options?: {
    id?: string;
    mode?: "read" | "readwrite";
  }) => Promise<IterableDirectoryHandle>;
};

/**
 * Whether the browser can pick a folder with its empty sub-folders intact
 * (File System Access API: Chromium 86+). Elsewhere the folder picker falls back
 * to `<input webkitdirectory>`, which reports files only — empty folders there
 * can still be uploaded by dragging them in.
 */
export function canPickDirectoryWithEmptyFolders(): boolean {
  return typeof (window as WindowWithDirectoryPicker).showDirectoryPicker === "function";
}

/**
 * Opens the browser's folder picker and reads the chosen folder in full: files
 * with paths rooted at the chosen folder's name (the same shape
 * `webkitRelativePath` has), and every directory, empty ones included.
 *
 * Resolves `null` when the user cancels. Must be called straight from a user
 * gesture — the picker needs transient activation — so callers must not await
 * anything before it. Only call when `canPickDirectoryWithEmptyFolders()`.
 */
export async function pickDirectory(): Promise<UploadSelection | null> {
  const picker = (window as WindowWithDirectoryPicker).showDirectoryPicker;
  if (!picker) throw new Error("showDirectoryPicker is not supported in this browser");

  let root: IterableDirectoryHandle;
  try {
    root = await picker.call(window, { id: "franklin-folder-upload", mode: "read" });
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") return null;
    throw error;
  }

  const selection: UploadSelection = { files: [], relativePaths: [], directoryPaths: [] };
  const walk = async (dir: IterableDirectoryHandle, path: string): Promise<void> => {
    selection.directoryPaths.push(path);
    for await (const child of dir.values()) {
      const childPath = `${path}/${child.name}`;
      if (child.kind === "file") {
        selection.files.push(await child.getFile());
        selection.relativePaths.push(childPath);
      } else {
        await walk(child, childPath);
      }
    }
  };
  await walk(root, root.name);
  return selection;
}
